import React from "react"

import { Loader2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import { DiaryEntry } from "@/types/person"

interface DiaryEntryCardProps {
  entry: DiaryEntry
  onResolve?: (entry: DiaryEntry) => void
  pendingUpdate?: boolean
  compactSpacing?: boolean // For matching DiaryEntriesTab spacing
}

const getTagColor = (tag: string) => {
  switch (tag) {
    case "Serious Escalation":
      return "bg-red-100 text-red-800"
    case "Service Review & Config":
      return "bg-blue-100 text-blue-800"
    case "Driver Feedback":
      return "bg-green-100 text-green-800"
    case "No Show Call":
      return "bg-yellow-100 text-yellow-800"
    case "General Call":
      return "bg-palette-jungleGreen100 text-palette-jungleGreen800"
    case "Compliance Violation":
      return "bg-purple-100 text-purple-800"
    case "Scheduling":
      return "bg-orange-100 text-orange-800"
    case "Onboarding Admin":
      return "bg-cyan-100 text-cyan-800"
    case "Off-Boarding":
      return "bg-pink-100 text-pink-800"
    case "THD Check-in":
      return "bg-indigo-100 text-indigo-800"
    case "New Driver Hypercare":
      return "bg-teal-100 text-teal-800"
    case "Contractor Ended Engagement":
      return "bg-fuchsia-100 text-fuchsia-800"
    case "Unnotified Absence (UA) Calls":
      return "bg-lime-100 text-lime-800"
    default:
      return "bg-palette-jungleGreen100 text-palette-jungleGreen800"
  }
}

const formatDateTime = (dateTime: string) => new Date(dateTime).toLocaleString()

const getDisplayEntryId = (entryId: string) => {
  if (!entryId) return entryId
  const parts = entryId.split("-")
  // If first part looks like DD/MM/YYYY, drop it
  if (parts.length > 1 && /^\d{2}\/\d{2}\/\d{4}$/.test(parts[0])) {
    return parts.slice(1).join("-")
  }
  return entryId
}

const DiaryEntryCard: React.FC<DiaryEntryCardProps> = ({
  entry,
  onResolve,
  pendingUpdate,
  compactSpacing = false,
}) => {
  const displayId = getDisplayEntryId(entry.entryId)
  return (
    <Card className="w-full border rounded-xl border-palette-jungleGreen200 shadow-none bg-white">
      <CardHeader className={compactSpacing ? "p-4 pb-3" : "p-4"}>
        <div className="flex flex-row justify-between items-start">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-lg font-semibold text-palette-jungleGreen900">{displayId}</span>
          </div>
          <div className="flex flex-col items-end min-w-[100px]">
            <span className="text-sm text-palette-jungleGreen600">
              {formatDateTime(entry.callDateTime)}
            </span>
            {entry.author && (
              <span className="text-sm text-palette-jungleGreen400">by {entry.author}</span>
            )}
          </div>
        </div>
        <div className="flex flex-row flex-wrap gap-1">
          {entry.driverOpsTag && (
            <Badge
              className={`text-xs font-medium pointer-events-none ${getTagColor(entry.driverOpsTag)}`}
            >
              {entry.driverOpsTag}
            </Badge>
          )}
          {Array.isArray(entry.driverOpsSubTag) &&
            entry.driverOpsSubTag.map((subTag, idx) => (
              <Badge
                key={idx}
                className="text-xs font-medium pointer-events-none bg-palette-jungleGreen100 text-palette-jungleGreen800 border border-palette-jungleGreen200"
              >
                {subTag}
              </Badge>
            ))}
          {typeof entry.driverOpsSubTag === "string" && entry.driverOpsSubTag && (
            <Badge className="text-xs font-medium pointer-events-none bg-palette-jungleGreen100 text-palette-jungleGreen800 border border-palette-jungleGreen200">
              {entry.driverOpsSubTag}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className={compactSpacing ? "p-4 pt-0 space-y-2" : "p-4 py-2"}>
        <p
          className={
            compactSpacing
              ? "text-sm text-palette-jungleGreen700 whitespace-pre-line"
              : "text-base leading-relaxed whitespace-pre-line"
          }
        >
          {entry.content}
        </p>
      </CardContent>
      {onResolve && entry.followUpAction && !entry.followUpComplete && (
        <CardFooter className={compactSpacing ? "p-0" : "p-4"}>
          <div
            className={
              compactSpacing
                ? "w-full border-t pt-3 px-6 pb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2 bg-yellow-50 rounded-b-xl"
                : "w-full border border-yellow-200 rounded-xl bg-yellow-50 flex flex-col md:flex-row md:items-center md:justify-between gap-2 p-3"
            }
          >
            <span className="text-sm text-yellow-700 font-medium">
              Outstanding Action: {entry.followUpAction}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onResolve(entry)}
              disabled={pendingUpdate}
            >
              {pendingUpdate ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : compactSpacing ? (
                "Confirm Complete"
              ) : (
                "Mark Resolved"
              )}
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>
  )
}

export default DiaryEntryCard
